import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "../firebase/config";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { toast } from "react-toastify";

const AdminPanel = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    // Admin kontrolü
    if (user.email !== "j1listsinfo@gmail.com") {
      navigate("/");
      return;
    }

    fetchRequests();
  }, [user, navigate]);

  const fetchRequests = async () => {
    try {
      const requestsRef = collection(db, "registrationRequests");
      const q = query(requestsRef, where("status", "==", "pending"));
      const snapshot = await getDocs(q);
      const requestsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setRequests(requestsList);
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Failed to fetch registration requests");
    } finally {
      setLoading(false);
    }
  };

  const createUsername = (companyName) => {
    // Şirket adından username oluştur
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "") // Sadece harf ve rakamları tut
      .slice(0, 15); // Maximum 15 karakter
  };

  const handleApprove = async (request) => {
    try {
      setLoading(true);
      const username = createUsername(request.companyName);

      // Firebase Auth'da kullanıcı oluştur
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        request.email,
        request.password // Kullanıcının belirlediği şifreyi kullan
      );

      // Users collection'a kullanıcı bilgilerini ekle
      await addDoc(collection(db, "users"), {
        uid: userCredential.user.uid,
        username: username,
        usernameLower: username.toLowerCase(),
        email: request.email,
        phone: request.phone,
        businessAddress: request.businessAddress,
        businessType: request.businessType,
        facebookUrl: request.facebookUrl,
        instagramUrl: request.instagramUrl,
        fullName: request.fullName,
        companyName: request.companyName,
        createdAt: serverTimestamp(),
        isApproved: true,
      });

      // Registration request'i güncelle
      const requestRef = doc(db, "registrationRequests", request.id);
      await updateDoc(requestRef, {
        status: "approved",
        approvedAt: serverTimestamp(),
      });

      toast.success("User approved successfully! 🎉");
      fetchRequests();
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Failed to approve request: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, "registrationRequests", requestId));
      toast.success("Request rejected successfully");
      fetchRequests();
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error("Failed to reject request");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <div className='animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500'></div>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8'>
      <h1 className='text-3xl font-bold mb-8'>
        Admin Panel - Registration Requests
      </h1>

      {requests.length === 0 ? (
        <div className='text-center text-gray-600 py-8'>
          No pending registration requests
        </div>
      ) : (
        <div className='grid gap-6'>
          {requests.map((request) => (
            <div
              key={request.id}
              className='bg-white rounded-lg shadow-md p-6 border border-gray-200'>
              <div className='grid grid-cols-2 gap-4 mb-4'>
                <div>
                  <h3 className='font-semibold'>Company Name</h3>
                  <p>{request.companyName}</p>
                </div>
                <div>
                  <h3 className='font-semibold'>Contact Person</h3>
                  <p>{request.fullName}</p>
                </div>
                <div>
                  <h3 className='font-semibold'>Email</h3>
                  <p>{request.email}</p>
                </div>
                <div>
                  <h3 className='font-semibold'>Phone</h3>
                  <p>{request.phone}</p>
                </div>
                <div>
                  <h3 className='font-semibold'>Business Type</h3>
                  <p className='capitalize'>{request.businessType}</p>
                </div>
                <div>
                  <h3 className='font-semibold'>Business Address</h3>
                  <p>{request.businessAddress}</p>
                </div>
                {request.additionalInfo && (
                  <div className='col-span-2'>
                    <h3 className='font-semibold'>Additional Info</h3>
                    <p>{request.additionalInfo}</p>
                  </div>
                )}
              </div>

              <div className='flex justify-end gap-4 mt-4'>
                <button
                  onClick={() => handleReject(request.id)}
                  className='px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors'
                  disabled={loading}>
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(request)}
                  className='px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors'
                  disabled={loading}>
                  Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
